import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Center,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertCircle, IconChevronRight } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  isPinRequiredResponse,
  pinUnlockHeaders,
} from "../../utils/pinUnlockSession";
import RepPayStructureEditor from "./RepPayStructureEditor";

// The rep's pay deal, edited in place through the always-visible pay editor.
// `onPinRequired` fires when the API refuses the deal for want of a PIN unlock —
// the parent owns the lock state, so it re-locks and prompts again.
function RepDealTab({ repId, repName, onPinRequired, onUploadDocument }) {
  const { getVerifiedJWT } = useAuth();

  const [deal, setDeal] = useState(null);
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped after each save to remount the editor so it re-seeds from the
  // freshly-reloaded deal.
  const [reseed, setReseed] = useState(0);

  const fetchDeal = useCallback(
    async yearId => {
      try {
        setLoading(true);
        setError(null);
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            yearId
              ? `/v1/ps/user-deal/by-rep/${repId}?selling_year_id=${yearId}`
              : `/v1/ps/user-deal/by-rep/${repId}`
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
              // The deal is PIN-gated server-side; without a live unlock grant
              // the request is refused rather than answered with empty data.
              ...pinUnlockHeaders(),
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (isPinRequiredResponse(response, errorData)) {
            onPinRequired?.();
            return;
          }
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }
        const result = await response.json();
        setDeal(result?.data ?? result ?? null);
      } catch (err) {
        setError(err.message || "Failed to load deal");
        setDeal(null);
      } finally {
        setLoading(false);
      }
    },
    [repId, getVerifiedJWT, onPinRequired]
  );

  useEffect(() => {
    fetchDeal(null);
  }, [fetchDeal]);

  // The season picker's options; the deal read itself defaults to the rep's most
  // recent active deal when no year is passed.
  useEffect(() => {
    let cancelled = false;
    const fetchYears = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(getApiEndpoint("/v2/selling-year/list"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        });
        if (!response.ok) return;
        const result = await response.json();
        const years = result?.data ?? result ?? [];
        if (!cancelled && Array.isArray(years)) setSellingYears(years);
      } catch (err) {
        // Without the year list the tab still shows the current deal.
      }
    };
    fetchYears();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT]);

  const dealUsers = useMemo(
    () => (Array.isArray(deal?.deal_users) ? deal.deal_users : []),
    [deal]
  );
  const isSplit = dealUsers.length > 1;

  // The season shown in the header: the picked year, else the active/first one.
  const currentSeason = sellingYears.find(
    y => String(y.id) === String(selectedYearId)
  );
  const seasonLabel =
    currentSeason?.name ||
    sellingYears.find(y => y.is_active)?.name ||
    sellingYears[0]?.name ||
    "Current season";

  const header = (
    <Group justify="space-between" align="center" wrap="wrap">
      <Group gap="sm" align="center" wrap="wrap">
        {sellingYears.length > 0 ? (
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <UnstyledButton aria-label="Change season">
                <Group gap={4} align="center" wrap="nowrap">
                  <Text fw={700} size="lg">
                    {seasonLabel}
                  </Text>
                  <IconChevronRight size={18} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Season</Menu.Label>
              {sellingYears.map(y => (
                <Menu.Item
                  key={y.id}
                  onClick={() => {
                    setSelectedYearId(String(y.id));
                    fetchDeal(String(y.id));
                  }}
                >
                  {y.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Text fw={700} size="lg">
            {seasonLabel}
          </Text>
        )}
        {isSplit && (
          <Badge color="gray" variant="light">
            Split
          </Badge>
        )}
      </Group>
      {onUploadDocument && (
        // Styled identically to the Documents tab's "+ Upload Document" button.
        <button
          type="button"
          onClick={onUploadDocument}
          style={{
            padding: "10px 20px",
            background: "#d0521f",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          + Upload Deal Document
        </button>
      )}
    </Group>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Center py="xl">
          <Loader />
        </Center>
      );
    }

    if (error) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      );
    }

    // The pay editor is always shown: it seeds from the current deal when one
    // exists, and starts empty (creating a document-less active deal on save)
    // when none does. Remount on save/season change so it re-seeds.
    return (
      <RepPayStructureEditor
        key={`${deal?.deal_id ?? "new"}-${selectedYearId ?? "current"}-${reseed}`}
        repId={repId}
        repName={repName}
        deal={deal}
        sellingYearId={selectedYearId}
        onSaved={() => {
          setReseed(n => n + 1);
          fetchDeal(selectedYearId);
        }}
        onPinRequired={onPinRequired}
      />
    );
  };

  return (
    <Stack gap="lg" p="lg">
      {header}
      {renderBody()}
    </Stack>
  );
}

export default RepDealTab;
